/**
 * Authentication API endpoints
 */

import apiClient from "../apiClient";
import {
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  ResetPasswordRequest,
  ResetPasswordConfirm,
  User,
  GoogleLoginRequest,
  GoogleLoginResponse,
  MagicLinkRequestPayload,
  MagicLinkRequestResponse,
  MagicLinkVerifyPayload,
  MagicLinkVerifyResponse,
  APIErrorResponse,
} from "@/types/auth.types";
import { ApiResponse } from "@/types/api.types";
import { AxiosError } from "axios";
import { logger } from "@/utils/logger";
/**
 * Login user
 */
export const login = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  const response = await apiClient.post<ApiResponse<LoginResponse>>(
    "/auth/login",
    credentials,
    { skipAuth: true } // Skip auth for login endpoint
  );
  return response.data;
};

/**
 * Register new user
 */
export const register = async (
  credentials: RegisterCredentials
): Promise<LoginResponse> => {
  const response = await apiClient.post<ApiResponse<LoginResponse>>(
    "/auth/register",
    credentials,
    { skipAuth: true }
  );
  return response.data;
};

/**
 * Logout user
 * Django Allauth Headless: DELETE /auth/app/v1/auth/session
 */
export const logout = async (): Promise<void> => {
  // Usar endpoint correto do Django Allauth Headless
  // DELETE /auth/app/v1/auth/session
  const axios = (await import("axios")).default;
  const { config } = await import("@/config/env");

  const baseUrl = config.backendBaseUrl;
  const fullUrl = baseUrl.replace("/api/v2/", "/") + "auth/app/v1/auth/session";

  logger.log("🔴 [LOGOUT] Deleting session at:", fullUrl);

  try {
    const sessionToken = localStorage.getItem("sessionToken");
    const response = await axios.delete(fullUrl, {
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": sessionToken || "",
      },
    });
    logger.log("✅ [LOGOUT] Session deleted successfully:", response.status);
  } catch (error: any) {
    logger.error(
      "❌ [LOGOUT] Failed to delete session:",
      error.response?.status
    );
    // Continuar mesmo se falhar - limpar frontend de qualquer forma
  }
};

/**
 * Get current user profile
 * Uses Django Allauth Headless session endpoint: GET /auth/app/v1/auth/session
 */
export const getCurrentUser = async (): Promise<User> => {
  const axios = (await import("axios")).default;
  const { config } = await import("@/config/env");
  const { getSessionToken } = await import("@/utils/storage");

  const baseUrl = config.backendBaseUrl;
  // Ensure we construct the correct URL for headless auth
  const fullUrl = baseUrl.replace("/api/v2/", "/") + "auth/app/v1/auth/session";
  const token = getSessionToken();

  try {
    const response = await axios.get(fullUrl, {
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": token || "",
      },
    });

    // The response structure is { status: 200, data: { user: ... }, meta: ... }
    const backendUser = response.data.data.user;

    // Map to User interface
    return {
      ...backendUser,
      avatar: backendUser.photo,
      name: backendUser.name || backendUser.display,
    } as User;
  } catch (error: any) {
    logger.error("❌ [Auth] Failed to get current user:", error);
    throw error;
  }
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (
  data: ResetPasswordRequest
): Promise<void> => {
  await apiClient.post("/auth/reset-password", data, { skipAuth: true });
};

/**
 * Confirm password reset
 */
export const confirmPasswordReset = async (
  data: ResetPasswordConfirm
): Promise<void> => {
  await apiClient.post("/auth/reset-password/confirm", data, {
    skipAuth: true,
  });
};

/**
 * Refresh session token
 */
export const refreshToken = async (): Promise<LoginResponse> => {
  const response =
    await apiClient.post<ApiResponse<LoginResponse>>("/auth/refresh");
  return response.data;
};

/**
 * Verify email
 */
export const verifyEmail = async (token: string): Promise<void> => {
  await apiClient.post(`/auth/verify-email/${token}`, {}, { skipAuth: true });
};

/**
 * Google OAuth Provider Token Exchange (Django Allauth Headless)
 * Exchanges Google credential for session token
 */
export const googleProviderToken = async (
  request: GoogleLoginRequest
): Promise<GoogleLoginResponse> => {
  // Import axios and config
  const axios = (await import("axios")).default;
  const { config } = await import("@/config/env");

  // Construct full URL without /api/v2 prefix
  const baseUrl = config.backendBaseUrl;
  const fullUrl =
    baseUrl.replace("/api/v2/", "/") + "auth/app/v1/auth/provider/token";

  logger.log("=== GOOGLE AUTH REQUEST ===");
  logger.log("URL:", fullUrl);
  logger.log("Request body:", JSON.stringify(request, null, 2));
  logger.log("=== END REQUEST ===");

  try {
    const response = await axios.post<GoogleLoginResponse>(fullUrl, request, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    logger.log("=== GOOGLE AUTH RESPONSE ===");
    logger.log("Response:", JSON.stringify(response.data, null, 2));
    logger.log("=== END RESPONSE ===");

    return response.data;
  } catch (error: any) {
    logger.error("=== GOOGLE AUTH ERROR ===");
    logger.error("Status:", error.response?.status);
    logger.error("Data:", JSON.stringify(error.response?.data, null, 2));
    logger.error("Errors array:", error.response?.data?.errors);
    logger.error("=== END ERROR ===");
    throw error;
  }
};

/**
 * Request a magic link to be sent to the provided email
 * Django Allauth Headless Passwordless Authentication
 */
export const requestMagicLink = async (
  payload: MagicLinkRequestPayload
): Promise<MagicLinkRequestResponse> => {
  try {
    const response = await apiClient.post<MagicLinkRequestResponse>(
      "/auth/passwordless/request",
      payload,
      { skipAuth: true } // No authentication required for requesting magic link
    );

    return response;
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;

    throw new Error(
      axiosError.response?.data?.error ||
        "Failed to send magic link. Please try again."
    );
  }
};

/**
 * Verify a magic link code and authenticate the user
 * Django Allauth Headless Passwordless Authentication
 */
export const verifyMagicLink = async (
  payload: MagicLinkVerifyPayload
): Promise<MagicLinkVerifyResponse> => {
  try {
    const response = await apiClient.post<MagicLinkVerifyResponse>(
      "/auth/passwordless/verify",
      payload,
      { skipAuth: true } // No authentication required for verifying magic link
    );

    return response;
  } catch (error) {
    const axiosError = error as AxiosError<APIErrorResponse>;

    throw new Error(
      axiosError.response?.data?.error ||
        "Failed to verify magic link. The link may be invalid or expired."
    );
  }
};
